import { Component, Input } from '@angular/core';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import esLocale from '@fullcalendar/core/locales/es';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { ITimekitComponent } from '../timekit-tabs/timekit-tabs.component';
import { ApiService } from 'src/app/api.service';
import { ThemingService } from 'src/app/theming.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LambdaService } from 'src/app/lambda.service';


interface Slot {
  start: string;
  end: string;
  resources: { name: string; id: string; timezone: string }[];
}

interface ApiResponse {
  statusCode: number;
  body: string;
  headers?: { [key: string]: string };
}

interface Project {
  timekitProjectId: number;
  shopId: number;
  timekitResourceId: number;
  projectId: string;
  projectName: string;
}

type ApiResponseData = Slot[];


@Component({
  selector: 'app-timekit-reschedule',
  templateUrl: './timekit-reschedule.component.html',
  styleUrls: ['./timekit-reschedule.component.scss'],
})

export class TimekitRescheduleComponent implements ITimekitComponent {
  @Input() data: any;
  public projects: Project[] = [];
  public selectedProjectId: string = '';
  public form: FormGroup;
  isModalOpen: boolean = false;
  availableSlots: any[] = [];
  availabilityResponse: any;
  selectedAppointment: any;
  isAvailabilityModalOpen: boolean = false;
  modalCancelAppointment = false;
  modalSuccess = false;
  availabilityData: any;
  projectId: string | undefined;
  loading: boolean = false;
  resources: any[] = [];
  selectedResourceId: string = '';

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, bootstrap5Plugin, momentTimezonePlugin],
    timeZone: 'America/Costa_Rica',
    themeSystem: 'bootstrap5',
    initialView: 'dayGridMonth',
    locale: esLocale,
    events: [],
    eventTimeFormat: {
      hour: 'numeric',
      minute: '2-digit',
      meridiem: true,
    },
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
    },
    eventClick: this.handleEventClick.bind(this),
  };

  constructor(
    private apiService: ApiService,
    public themingService: ThemingService,
    private fb: FormBuilder,
    private lambdaService: LambdaService
  ) {
    this.form = this.fb.group({
      resources: ['']
    });
   }

  ngOnInit() {
    this.loadAppointments();
    this.getTimekitProjects();
    this.getTimekitResources();
  }

  getFormattedDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { weekday: 'long', month: 'long', day: 'numeric' });
  }

  getFormattedTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  async getTimekitProjects() {
    try {
      const response = await this.lambdaService.getTimekitProjects(this.themingService.shopId) as { data: Project[] };
      this.projects = response.data || [];
      if (this.projects.length > 0) {
        this.selectedProjectId = this.projects[0].projectId;
        if (this.resources.length === 0) {
          await this.getTimekitResources();
        }
        this.extractResourceIdFromProjectId(this.selectedProjectId);
        await this.getAvailability();
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      this.projects = [];
    }
  }

  async getTimekitResources() {
    this.loading = true;
    try {
      const response = await this.lambdaService.getTimekitResources(this.themingService.shopId) as { data: { resourceId: string, resourceName: string }[] };
      this.resources = response.data || [];
    } catch (error) {
      console.error('Error fetching resources:', error);
      this.resources = [];
    } finally {
      this.loading = false;
    }
  }

  onProjectChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedProjectId = selectElement.value;
    this.projectId = this.selectedProjectId;
    this.extractResourceIdFromProjectId(this.selectedProjectId);

    if (this.selectedProjectId) {
      this.getAvailability();
    } else {
      this.availabilityData = [];
      this.calendarOptions.events = [];
    }
  }

  extractResourceIdFromProjectId(projectId: string) {
    const selectedProject = this.projects.find(p => p.projectId === projectId);
    if (!selectedProject) return;

    const timekitResourceId = selectedProject.timekitResourceId;
    const matchingResource = this.resources.find(
      r => String(r.timekitResourceId) === String(timekitResourceId) 
    );

    if (matchingResource) {
      this.selectedResourceId = matchingResource.resourceId;
    } else {
      console.warn('No se encontró matchingResource para:', timekitResourceId);
    }
  }


  async getAvailability() {
    try {
      const response = await this.apiService.getAvailability(
        this.themingService.shopId,
        this.selectedProjectId
      ) as any;
      let data: any;
      if (typeof response?.body === 'string') {
        try {
          data = JSON.parse(response.body);
        } catch (err) {
          console.error('Error parsing JSON body:', err);
          return;
        }
      } else if (response?.body) {
        data = response.body;
      } else if (Array.isArray(response)) {
        data = response; // 🔹 Caso: la API ya devuelve array directamente
      } else {
        data = response?.data ?? [];
      }
      if (Array.isArray(data)) {
        this.availabilityData = data;
        this.loadAppointments();
      } else {
        console.error('Availability data is not an array:', data);
      }
    } catch (error) {
      console.error('Error getting availability:', error);
    }
  }

  loadAppointments() {
    if (!this.availabilityData || !Array.isArray(this.availabilityData)) {
      return;
    }

    const events = this.availabilityData.map((appointment: any) => {
      const start = new Date(appointment.attributes.event_info.start);
      const end = new Date(appointment.attributes.event_info.end);

      return {
        id: appointment.id,
        title: appointment.customers?.[0]?.name || 'Nombre no disponible',
        start: start.toISOString(),
        end: end.toISOString(),
        formattedStartDate: this.getFormattedDate(start.toISOString()),
        formattedStartTime: this.getFormattedTime(start.toISOString()),
        formattedEndTime: this.getFormattedTime(end.toISOString()),
        display: 'block',
        className: 'my-custom-event',
        extendedProps: { appointment }
      };
    });

    this.calendarOptions.events = events;
  }

  handleEventClick(arg: any) {
    const selected = arg.event.extendedProps.appointment;

    if (selected) {
      this.selectedAppointment = {
        id: selected.id || arg.event.id,
        state: selected.state || 'unknown',
        graph: selected.graph || 'unknown',
        completed: selected.completed || false,
        created_at: selected.created_at || new Date().toISOString(),
        updated_at: selected.updated_at || new Date().toISOString(),
        location: selected.attributes?.event_info?.where || '',
        customers: [{
          name: selected.attributes?.customer?.name || 'No disponible',
          email: selected.attributes?.customer?.email || 'No disponible',
          telefono: selected.attributes?.customer?.telefono || 'No disponible',
          lugar_de_cita: selected.attributes?.customer?.lugar_de_cita || 'No disponible',
          tipocita: selected.attributes?.customer?.tipo_de_cita || 'No disponible'
        }],
        start: selected.attributes?.event_info?.start || new Date().toISOString(),
        end: selected.attributes?.event_info?.end || new Date(new Date().getTime() + 60 * 60 * 1000).toISOString()
      };

      this.openModal();
    }
  }

  openModal() {
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  async getBookings(): Promise<any[]> {
    try {
      const response = await this.apiService.getBookings(
        this.themingService.shopId,
        this.selectedProjectId
      );
      const data = Array.isArray(response) ? response : [];
      return data;
    } catch (error) {
      console.error('Error fetching bookings:', error);
      throw error;
    }
  }

  async updateAppointment() {
    if (this.selectedAppointment) {
      if (!this.selectedAppointment.start || !this.selectedAppointment.end) {
        return;
      }

      try {
        const responseData = await this.getBookings();

        if (Array.isArray(responseData)) {
          this.availableSlots = responseData.map(slot => {
            const formattedStart = this.getFormattedDate(slot.start);
            const startTime = this.getFormattedTime(slot.start);
            const endTime = this.getFormattedTime(slot.end);

            return {
              ...slot,
              formattedStart,
              startTime,
              endTime
            };
          });
        } else {
          this.availableSlots = [];
        }

        if (this.availableSlots.length === 0) {
        }
        this.openAvailabilityModal();

      } catch (error) {
        console.error('Error checking availability:', error);
        this.availableSlots = [];
        this.openAvailabilityModal();
      }
    }

    setTimeout(() => {
      this.isModalOpen = false;
      this.loadAppointments();
    }, 100);
  }

  openAvailabilityModal() {
    this.isAvailabilityModalOpen = true;
  }

  closeAvailabilityModal() {
    this.isAvailabilityModalOpen = false;
  }

  async rescheduleBookings() {
    try {
      const resourceId = this.selectedResourceId;
      const { start, end, id } = this.selectedAppointment;
      const response = await this.apiService.rescheduleBookings(
        this.themingService.shopId,
        this.selectedProjectId,
        resourceId,
        id,
        start,
        end
      );
      return response;
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      throw error;
    }
  }

  selectSlot(slot: Slot) {
    if (this.selectedAppointment && this.selectedAppointment.id) {
      this.selectedAppointment.start = slot.start;
      this.selectedAppointment.end = slot.end;

      this.closeAvailabilityModal();

      this.rescheduleBookings()
        .then(data => {
          this.getAvailability();
        })
        .catch(error => {
          console.error('Error rescheduling appointment:', error);
        });
    } else {
      console.error('Error: No appointment selected or appointment ID is undefined');
    }
  }

  private handleError(response: Response) {
    if (!response.ok) {
      return response.text().then(text => {
        throw new Error(`Error ${response.status}: ${response.statusText}\n${text}`);
      });
    }
    return response;
  }

  async cancelBooking() {
    try {
      const { id } = this.selectedAppointment;
      const response = await this.apiService.cancelBooking(
        this.themingService.shopId,
        this.selectedProjectId,
        id
      );

      await this.apiService.deleteSessions(
        this.themingService.shopId,
        id
      )

      return response;
    } catch (error) {
      console.error('Error canceling appointment:', error);
      throw error;
    }
  }

  cancelAppointment() {
    if (this.selectedAppointment) {
      this.modalCancelAppointment = true;
    }
  }

  async handleCancelConfirmation(confirm: boolean) {
    if (confirm && this.selectedAppointment) {
      try {
        await this.cancelBooking();
        this.modalCancelAppointment = false;
        this.modalSuccess = true;
        this.isModalOpen = false;
        this.getAvailability();
      } catch (error) {
        console.error('Error during cancel confirmation:', error);
        this.modalCancelAppointment = false;
      }
    } else {
      this.modalCancelAppointment = false;
    }
  }

  closeModalAppointment() {
    this.modalSuccess = false;
  }
}
